import KeywordFilter from "components/filters/Filters/KeywordFilter";
import ListTemplate from "components/ListTemplate";
import TwoColumnLayout from "layout/TwoColumnLayout";
import React, { useState } from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { mapFilterValuesToInstruction, mapResponseToFilterValues } from "./helpers";
import RightSideNavigation from "components/RightSideNavigation/RightSideNavigation";
import classNames from "classnames";
import CultureFlag from "components/Culture/CultureFlag";
import UserWithAvatar from "components/Atoms/UserWithAvatar";
import Tag from "components/Tag/Tag";
import FreefieldProfileFilter from "components/filters/Filters/FreefieldProfileFilter";
import UserToClientLifecycleFilter from "components/filters/Filters/UserToClientLifecycleFilter";
import { PhosphorWarningCircle } from "components/Atoms/PhosphorIcon";
import LimitedList from "components/LimitedList";
import { useLoaderData } from "react-router";
import { NewUserForInternalModal } from "../NewUserForInternalModal";

const PermissionCircle = ({ hasPermission }: { hasPermission: boolean }) => {
  return <i className={classNames("icon-circle", { red: !hasPermission, green: hasPermission })}></i>
}

const UserForInternalAdminListApp = () => {
  const viewModel = useLoaderData<server.viewmodel.InternalUserListFrontModel>();
  const isActiveUser = (item: server.dto.UserForInternalAdminForList) => item.lifeCycle === "active";
  const [isNewOpen, setIsNewOpen] = useState(false);

  return <>
    <TwoColumnLayout>
      <TwoColumnLayout.LeftColumn>
        <ListTemplate
          title={viewModel.pageTitle}
          filterSet="userForInternalAdmin_List"
          defaultFilters={viewModel.defaultFilters}
          initialInstruction={viewModel.initialInstruction}
          fetchFunction={window.CoreApi.UserForInternalAdmin.Paginated}
          getId={item => item.userToClientId}
          onNew={() => setIsNewOpen(true)}
          layout={{
            type: "table",
            columns: [
              {
                header: RESX.GeneralLabels.Name, key: "name", render: item =>
                  <UserWithAvatar user={item.user} nameHref={routes.internalUser_Detail(item.userToClientId).absolute}>
                    {!isActiveUser(item) && <div><Tag color="red" label={RESX.SoUser.Deactivated} /></div>}
                    {(isActiveUser(item) && item.artists.length > 0) && <div className="s1_red s1_small">
                      {RESX.SoUser.LimitedAccess}: <LimitedList
                        items={item.artists}
                        renderItem={artist => artist}
                        renderExcess={nr => ` (+${nr})`}
                        limit={5}
                        delimiter=", "
                      />
                    </div>}
                  </UserWithAvatar>
              },
              { header: site.currentEnvironment.contract.singular, align: "center", key: "contract", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canAccessContracts} /> },
              { header: site.currentEnvironment.invoice.singular, align: "center", key: "invoice", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canAccessInvoices} /> },
              { header: RESX.Payment.resxPayment, key: "payment", align: "center", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canModifyPayments} /> },
              { header: RESX.GeneralLabels.SystemSettings, key: "systemSettings", align: "center", render: item => isActiveUser(item) && <PermissionCircle hasPermission={item.canConfigureSystemSettings} /> },
              {
                header: RESX.Bookkeeping.Bookkeepings,
                key: "bookkeeping",
                align: "center",
                render: item => {
                  if (!isActiveUser(item)) return <></>
                  if (item.bookkeepingsCount !== 0) return item.bookkeepingsCount;
                  return <PhosphorWarningCircle iconColor="error" iconSize="large" />
                }
              },
              {
                header: RESX.Feed.iCalendarFeeds,
                key: "iCalendarFeeds",
                align: "center",
                render: item => {
                  if (!isActiveUser(item) || item.iCalendarFeedCount === 0) return <></>
                  return item.iCalendarFeedCount;
                }
              },
              {
                header: RESX.FreeField.FreeFields,
                key: "freeFields",
                render: item => {
                  if (!isActiveUser(item)) return <></>
                  if (item.freeFieldProfile == null) return <PhosphorWarningCircle iconColor="error" iconSize="large" />
                  return <>{item.freeFieldProfile.title}</>
                }
              },
              { header: "", key: "cultureFlag", render: item => isActiveUser(item) && <CultureFlag lcid={item.user.culture} /> },
            ]
          }}
          filters={<>
            <KeywordFilter placeholder={RESX.GeneralLabels.Search} />
            <UserToClientLifecycleFilter />
            <FreefieldProfileFilter environment="internal" />
          </>}
          functions={{
            mapResponseToFilterValues,
            mapFilterValuesToInstruction
          }}
        />
      </TwoColumnLayout.LeftColumn>
      <RightSideNavigation
        title={desktopNavigation.rightSideNavigation.title}
        menuItems={desktopNavigation.rightSideNavigation.items}
        currentPage={desktopNavigation.currentPage}
      />
    </TwoColumnLayout>
    <NewUserForInternalModal
      isOpen={isNewOpen}
      onClose={() => setIsNewOpen(false)}
    />
  </>
};


export default UserForInternalAdminListApp;